import { Col, FlexView, Row, Separator } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import { Table, TableCell } from 'comps';
import Pagination from 'modules/list/pagination';
import Search from 'modules/list/new-search-bar';
import React, { useCallback, useEffect, useState } from 'react';
import { Loader, Menu, Popup, TableRow } from 'semantic-ui-react';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import { useGetAllEvalPrepTasks } from 'actions/eval-prep-visibility';
import { MonoText, PageHeader, RegionPicker } from '../common/filters';
import PastRangePicker, {
  defaultPastRange,
  pastRangeToEpochs,
} from '../common/past-range-picker';
import {
  PREP_STATUSES,
  PREP_STATUS_HINTS,
  PREP_STEPS,
  formatEpochIst,
} from '../constants';
import PrepTaskDetail from './detail';

const PER_PAGE = 20;

const Container = styled(FlexView)`
  padding: 0 16px;
  width: 100%;
`;
const TableWrapper = styled(Row)`
  margin-top: 16px;
  width: 100%;
`;
// gap on Row is vertical padding, not a flex gap, so the spacing is set here.
const ControlRow = styled(Row)`
  flex-wrap: wrap;
  gap: 12px;
  row-gap: 10px;
  padding: 6px 0 10px;
`;
const ClickableRow = styled(TableRow)`
  cursor: pointer;
`;
const Stats = styled(Row)`
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;
const Stat = styled(Col)`
  border: 1px solid ${COLORS.GRAY_LIGHT};
  border-radius: 6px;
  padding: 8px 14px;
  min-width: 96px;
  flex: 0 0 auto;
`;
const Chip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  white-space: nowrap;
  background: ${({ tone }) => tone.bg};
  color: ${({ tone }) => tone.fg};
`;

const TONES = {
  COMPLETED: { bg: '#e8f3ed', fg: COLORS.DARK_GREEN },
  RAISED: { bg: '#f6f6f6', fg: COLORS.GRAY },
  OWED: { bg: '#f6f6f6', fg: COLORS.GRAY },
  OVERDUE: { bg: '#f8ebeb', fg: COLORS.RUSTY_RED },
  NEVER_PUSHED: { bg: '#f8ebeb', fg: COLORS.RUSTY_RED },
};

// Readiness only arms 12h before a first class, so a student without one owes
// nothing yet -- say that rather than showing a bare dash.
const hintedChip = (status, label) => (
  <Popup
    inverted
    size="small"
    position="top center"
    content={PREP_STATUS_HINTS[status]}
    trigger={<Chip tone={TONES[status] || TONES.OWED}>{label}</Chip>}
  />
);

const stepChip = step => {
  if (!step || (!step.raised && !step.owed)) {
    return hintedChip('NOT_OWED', 'not owed');
  }
  const status = step.status || 'OWED';

  return hintedChip(status, status.toLowerCase());
};

const EvalPrepTasks = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(null);
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState(null);
  const [range, setRange] = useState(defaultPastRange);
  const [selected, setSelected] = useState(null);

  const {
    data,
    getAll: getPrepTasks,
    isProcessing,
    error: loadError,
  } = useGetAllEvalPrepTasks();

  const load = useCallback(() => {
    getPrepTasks({
      page,
      per_page: PER_PAGE,
      ...(status ? { status } : {}),
      ...(step ? { step } : {}),
      ...(search ? { intel_enrollment_id: search } : {}),
      ...(region ? { region } : {}),
      ...pastRangeToEpochs(range),
    });
  }, [getPrepTasks, page, status, step, search, region, range]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.rows || [];
  const summary = data?.summary || {};
  const total = data?.pagination?.total ?? null;

  const onStatus = useCallback(next => {
    setStatus(next);
    setPage(1);
  }, []);

  const onRegion = useCallback(next => {
    setRegion(next);
    setPage(1);
  }, []);

  return (
    <Container column>
      <PageHeader title="Prep Tasks" />

      <Stats>
        {PREP_STATUSES.map(({ key, label, hint }) => (
          <Popup
            key={key}
            inverted
            size="small"
            position="bottom left"
            content={hint}
            trigger={
              <Stat>
                <Text variant="h6">{summary[key.toLowerCase()] ?? 0}</Text>
                <Text variant="body3" color={COLORS.GRAY}>
                  {label}
                </Text>
              </Stat>
            }
          />
        ))}
      </Stats>

      <Separator transparent height={12} />
      <Menu pointing secondary>
        <Menu.Item active={!status} onClick={() => onStatus(null)} content="All" />
        {PREP_STATUSES.map(({ key, label, hint }) => (
          <Popup
            key={key}
            inverted
            size="small"
            position="bottom center"
            content={hint}
            trigger={
              <Menu.Item
                active={status === key}
                onClick={() => onStatus(key)}
                content={label}
              />
            }
          />
        ))}
      </Menu>

      <ControlRow gutter={0} alignItems="center">
        <PastRangePicker value={range} onChange={setRange} />
        <RegionPicker value={region} onChange={onRegion} />
        <Menu compact size="tiny">
          <Menu.Item active={!step} onClick={() => setStep(null)} content="Both steps" />
          {PREP_STEPS.map(({ key, label }) => (
            <Menu.Item
              key={key}
              active={step === key}
              onClick={() => setStep(key)}
              content={label}
            />
          ))}
        </Menu>
        <Search placeholder="Search by enrollment ID" onSearch={setSearch} />
      </ControlRow>

      <TableWrapper>
        {isProcessing ? (
          <Loader active inline="centered">
            Loading prep tasks...
          </Loader>
        ) : loadError ? (
          <Text variant="body2" color={COLORS.RUSTY_RED}>
            {loadError.message || 'Could not load prep tasks.'}
          </Text>
        ) : (
          <Table
            headerCell={[
              { title: 'Student' },
              { title: 'Coach' },
              { title: 'Mapping state' },
              { title: 'Handoff' },
              { title: 'Readiness' },
              { title: 'Handoff due' },
            ]}
            dataLength={rows.length}
            havePagination
            scrollBodyHeight={
              isProcessing || !rows.length ? '400px' : 'calc(100vh - 320px)'
            }
            showInfo={false}
            showLoader={isProcessing}
            flexBasis="100%"
            noDataMessage="No eval students owe prep in this window."
          >
            {rows.map(row => (
              <ClickableRow
                key={row.intel_enrollment_id}
                onClick={() => setSelected(row.intel_enrollment_id)}
              >
                <TableCell width={4} textTransform="none">
                  {row.student_name || '-'}
                  {row.grade ? ` · Gr ${row.grade}` : ''}
                  <Separator transparent height={2} />
                  <MonoText>{row.intel_enrollment_id}</MonoText>
                </TableCell>
                <TableCell width={4} textTransform="none">
                  {row.teacher_name || '-'}
                  <Separator transparent height={2} />
                  <MonoText>{row.teacher_id || '-'}</MonoText>
                </TableCell>
                <TableCell width={3} textTransform="none">
                  <MonoText>{row.state}</MonoText>
                </TableCell>
                <TableCell width={2}>{stepChip(row.handoff)}</TableCell>
                <TableCell width={2}>{stepChip(row.readiness)}</TableCell>
                <TableCell width={3} textTransform="none">
                  <MonoText>{formatEpochIst(row.handoff?.deadline_at)}</MonoText>
                </TableCell>
              </ClickableRow>
            ))}
          </Table>
        )}
      </TableWrapper>

      <Separator transparent height={8} />
      <Row gutter={0} justifyContent="center">
        {total > PER_PAGE && (
          <Pagination
            page={page}
            perPage={PER_PAGE}
            count={total}
            onPageChange={setPage}
            shouldShowNewUi
          />
        )}
      </Row>

      {selected && (
        <PrepTaskDetail
          intelEnrollmentId={selected}
          closeModal={() => setSelected(null)}
        />
      )}
    </Container>
  );
};

export default EvalPrepTasks;
